import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { customInstance } from "@/lib/axios";
import { PASSWORD_MIN_LENGTH, validatePasswordClient } from "@/lib/password-requirements";
import { PasswordRequirementChecklist } from "@/components/PasswordRequirementChecklist";

export function AccountPasswordPage() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [touchedPassword, setTouchedPassword] = useState(false);

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (touchedPassword) {
      setPasswordErrors(value ? validatePasswordClient(value) : []);
    } else {
      setPasswordErrors([]);
    }
    if (confirmPassword && value !== confirmPassword) {
      setConfirmError("Passwords do not match.");
    } else {
      setConfirmError(null);
    }
  };

  const handlePasswordBlur = () => {
    setTouchedPassword(true);
    if (password) setPasswordErrors(validatePasswordClient(password));
  };

  const handleConfirmChange = (value: string) => {
    setConfirmPassword(value);
    setConfirmError(value && password !== value ? "Passwords do not match." : null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword.trim()) {
      toast.error("Enter your current password.");
      return;
    }
    const pwdErrors = validatePasswordClient(password);
    const matchErr = password !== confirmPassword ? "Passwords do not match." : null;
    setPasswordErrors(pwdErrors);
    setConfirmError(matchErr);
    setTouchedPassword(true);
    if (pwdErrors.length > 0 || matchErr) {
      toast.error("Please fix the errors below.");
      return;
    }
    setIsLoading(true);
    setPasswordErrors([]);
    setConfirmError(null);
    try {
      await customInstance.post("/api/auth/password-change/", {
        old_password: currentPassword,
        new_password: password,
      });
      toast.success("Password updated.");
      setCurrentPassword("");
      setPassword("");
      setConfirmPassword("");
      setTouchedPassword(false);
    } catch (err: any) {
      const msg = err?.response?.data?.error;
      const apiPwd = err?.response?.data?.password;
      const list = Array.isArray(apiPwd) ? apiPwd : apiPwd ? [apiPwd] : [];
      if (list.length > 0) {
        setPasswordErrors(list);
        toast.error(list[0]);
      } else {
        toast.error(msg || "Could not update password.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const hasPasswordError = passwordErrors.length > 0;

  return (
    <div className="max-w-lg mx-auto space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Change password</h1>
        <p className="mt-1 text-sm text-gray-600">Use a strong password you do not use elsewhere.</p>
        <p className="mt-2 text-sm">
          <Link to="/apps" className="text-primary hover:underline">
            ← Back to applications
          </Link>
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Update your password</CardTitle>
          <CardDescription>Enter your current password, then choose a new one.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current password</Label>
              <Input
                id="currentPassword"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">New password</Label>
              <PasswordRequirementChecklist password={password} />
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                onBlur={handlePasswordBlur}
                required
                minLength={PASSWORD_MIN_LENGTH}
                aria-describedby="password-requirements password-errors"
                aria-invalid={hasPasswordError}
                className={hasPasswordError ? "border-red-500 focus-visible:ring-red-500" : ""}
              />
              {hasPasswordError && (
                <div id="password-errors" className="text-sm text-red-600 font-medium space-y-0.5" role="alert">
                  {passwordErrors.map((msg, i) => (
                    <p key={i}>{msg}</p>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmNewPassword">Confirm new password</Label>
              <Input
                id="confirmNewPassword"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => handleConfirmChange(e.target.value)}
                required
                minLength={PASSWORD_MIN_LENGTH}
                aria-describedby={confirmError ? "confirm-error" : undefined}
                aria-invalid={!!confirmError}
                className={confirmError ? "border-red-500 focus-visible:ring-red-500" : ""}
              />
              {confirmError && (
                <p id="confirm-error" className="text-sm text-red-600 font-medium" role="alert">
                  {confirmError}
                </p>
              )}
            </div>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving…" : "Update password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
